import type { Chapter, Clip, Piece, Range } from '../queries/edit';

/**
 * The finished video, as a list of pieces in the order they play.
 *
 * A piece is a range of the original recording, or a range of another
 * recording dropped in as a clip. There is one timeline: splitting a piece
 * makes two, deleting one takes it out, dragging one moves it, and every
 * piece is trimmed the same way at its edges. The API keeps the same list
 * (services/edits.ts) and MediaConvert renders it in this order, so what is
 * drawn here is what comes out.
 *
 * Two clocks: a second inside a piece's source ("source time"), which is
 * what the transcript and the waveform speak, and a second in the finished
 * video ("output time"), which is what the playhead and the timeline speak.
 * `plan` places each piece on the output clock; the helpers translate.
 */

/** The shortest a piece can be trimmed to. The API enforces the same. */
export const MIN_PIECE_SEC = 0.2;
export const MIN_CLIP_SEC = MIN_PIECE_SEC;
/** As many clips as the API will take in one recording. */
export const MAX_CLIPS = 20;
const EPSILON = 0.001;

export interface Placed {
  piece: Piece;
  /** Position in the list. */
  index: number;
  /** Where the piece starts and ends in the finished video. */
  outStart: number;
  outEnd: number;
}

export function newId(prefix = 'p'): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Every self piece with an id, so the editor can key and follow it. */
export function withIds(pieces: Piece[]): Piece[] {
  return pieces.map((piece) => (piece.kind === 'self' && !piece.id ? { ...piece, id: newId('s') } : piece));
}

/** The list as the API takes it: self pieces lose their editor-only id. */
export function forApi(pieces: Piece[]): Piece[] {
  return pieces.map((piece) => {
    const title = piece.title?.trim();
    const base = piece.kind === 'self' ? { kind: 'self' as const, start: piece.start, end: piece.end } : { ...piece };
    if ('title' in base) delete base.title;
    return title ? { ...base, title } : base;
  });
}

/**
 * The chapters of the finished video: each titled piece begins one, and it
 * runs until the next titled piece, or the end. Pieces before the first
 * title belong to no chapter. The API works these out the same way for the
 * share page.
 */
export function chaptersOf(placed: Placed[]): Chapter[] {
  const total = plannedDuration(placed);
  const starts = placed.filter((one) => one.piece.title?.trim());
  return starts.map((one, i) => ({
    title: one.piece.title!.trim(),
    startSec: one.outStart,
    endSec: starts[i + 1]?.outStart ?? total,
  }));
}

/** The chapter a second of the finished video falls in, if any. */
export function chapterAt(chapters: Chapter[], sec: number): Chapter | null {
  for (let i = chapters.length - 1; i >= 0; i -= 1) if (sec >= chapters[i].startSec - 0.001) return chapters[i];
  return null;
}

/** One piece renamed. An empty name takes the chapter away. */
export function retitlePiece(pieces: Piece[], index: number, title: string): Piece[] {
  return pieces.map((piece, i) => {
    if (i !== index) return piece;
    const clean = title.trim();
    const next = { ...piece };
    if (clean) next.title = clean;
    else delete next.title;
    return next;
  });
}

export function pieceId(piece: Piece): string {
  return piece.id ?? `${piece.kind}-${piece.start}-${piece.end}`;
}

export function pieceLength(piece: Piece): number {
  return Math.max(0, piece.end - piece.start);
}

export function plan(pieces: Piece[]): Placed[] {
  let out = 0;
  return pieces.map((piece, index) => {
    const placed: Placed = { piece, index, outStart: out, outEnd: out + pieceLength(piece) };
    out = placed.outEnd;
    return placed;
  });
}

export function plannedDuration(placed: Placed[]): number {
  return placed.length > 0 ? placed[placed.length - 1].outEnd : 0;
}

/** The piece under a second of the finished video. The last one at the very end. */
export function placedAtOut(placed: Placed[], out: number): Placed | null {
  if (placed.length === 0) return null;
  for (const one of placed) if (out < one.outEnd - EPSILON) return one;
  return placed[placed.length - 1];
}

/** A second of the original, where it lands in the finished video. Null when cut. */
export function toOutput(sourceTime: number, placed: Placed[]): number | null {
  for (const one of placed) {
    if (one.piece.kind !== 'self') continue;
    if (sourceTime >= one.piece.start - EPSILON && sourceTime <= one.piece.end + EPSILON) {
      return one.outStart + Math.max(0, Math.min(sourceTime - one.piece.start, pieceLength(one.piece)));
    }
  }
  return null;
}

/**
 * A second of the original that is in no piece, read as the nearest output
 * second: the start of the first piece that plays material after it, or the
 * end when nothing does.
 */
export function nearestOutput(sourceTime: number, placed: Placed[]): number {
  let best: Placed | null = null;
  for (const one of placed) {
    if (one.piece.kind !== 'self' || one.piece.start < sourceTime) continue;
    if (!best || one.piece.start < (best.piece as { start: number }).start) best = one;
  }
  return best ? best.outStart : plannedDuration(placed);
}

/** The ranges of the original that survive, ascending: what the transcript strikes through against. */
export function selfRanges(pieces: Piece[]): Range[] {
  return pieces
    .flatMap((piece): Range[] => (piece.kind === 'self' ? [[piece.start, piece.end]] : []))
    .sort((a, b) => a[0] - b[0]);
}

export function clipCount(pieces: Piece[]): number {
  return pieces.reduce((n, piece) => n + (piece.kind === 'clip' ? 1 : 0), 0);
}

/** True when the list is the whole original, untouched, and nothing added. */
export function isWhole(pieces: Piece[], duration: number): boolean {
  return (
    pieces.length === 1 &&
    pieces[0].kind === 'self' &&
    pieces[0].start <= 0.05 &&
    pieces[0].end >= duration - 0.05
  );
}

/** True when a second of the original plays somewhere. */
export function isKept(sourceTime: number, pieces: Piece[]): boolean {
  return pieces.some(
    (piece) => piece.kind === 'self' && sourceTime >= piece.start - EPSILON && sourceTime < piece.end + EPSILON,
  );
}

// ---------------------------------------------------------------------------
// Editing, as pure functions over the list.

/** Two pieces where there was one, cut at a second inside it. */
export function splitPiece(pieces: Piece[], index: number, at: number): Piece[] {
  const piece = pieces[index];
  if (!piece) return pieces;
  if (at < piece.start + MIN_PIECE_SEC || at > piece.end - MIN_PIECE_SEC) return pieces;
  const first = { ...piece, end: at };
  // The second half is a new piece of the same chapter, not a chapter of its own.
  const second: Piece = { ...piece, id: newId(piece.kind === 'self' ? 's' : 'c'), start: at };
  delete second.title;
  return [...pieces.slice(0, index), first, second, ...pieces.slice(index + 1)];
}

/** The list without one piece. Null when that would leave nothing of the original. */
export function removePiece(pieces: Piece[], index: number): Piece[] | null {
  const next = pieces.filter((_, i) => i !== index);
  if (!next.some((piece) => piece.kind === 'self')) return null;
  return next;
}

/** One piece moved to another position; the rest keep their order. */
export function movePiece(pieces: Piece[], from: number, to: number): Piece[] {
  if (from === to || from < 0 || from >= pieces.length) return pieces;
  const next = [...pieces];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moved);
  return next;
}

/**
 * How far a piece's edges may go. A self piece may grow back over material
 * nothing else plays, up to the neighbouring self pieces in source time and
 * the ends of the original; a clip may grow to the ends of its source.
 */
export function trimBounds(
  pieces: Piece[],
  index: number,
  duration: number,
  sourceLength: number | null,
  /** A piece whose material does not count as taken: the neighbour an edge is shared with. */
  except?: number,
): { minStart: number; maxEnd: number } {
  const piece = pieces[index];
  if (!piece) return { minStart: 0, maxEnd: duration };
  const length = piece.kind === 'clip' ? (sourceLength ?? piece.end) : duration;
  let minStart = 0;
  let maxEnd = length;
  pieces.forEach((other, i) => {
    if (i === index || i === except || !sameSource(piece, other)) return;
    if (other.end <= piece.start + EPSILON) minStart = Math.max(minStart, other.end);
    if (other.start >= piece.end - EPSILON) maxEnd = Math.min(maxEnd, other.start);
  });
  return { minStart, maxEnd };
}

/** Two pieces of one recording: the original twice over, or the same clip source. */
export function sameSource(a: Piece, b: Piece): boolean {
  if (a.kind === 'self') return b.kind === 'self';
  return b.kind === 'clip' && a.videoId === b.videoId;
}

export function trimPiece(
  pieces: Piece[],
  index: number,
  edge: 'start' | 'end',
  to: number,
  bounds: { minStart: number; maxEnd: number },
): Piece[] {
  const piece = pieces[index];
  if (!piece) return pieces;
  const next =
    edge === 'start'
      ? { ...piece, start: Math.max(bounds.minStart, Math.min(to, piece.end - MIN_PIECE_SEC)) }
      : { ...piece, end: Math.min(bounds.maxEnd, Math.max(to, piece.start + MIN_PIECE_SEC)) };
  return pieces.map((one, i) => (i === index ? next : one));
}

/**
 * Dragging one edge of a piece.
 *
 * A handle never changes what is in the row, only where its boundaries
 * sit, so the total never moves and the boundary stays under the hand. The
 * edge between two neighbouring pieces is one boundary, whatever the two
 * are made of: pull it and one piece shrinks while the other grows by the
 * same amount. Two pieces of one recording may meet but never overlap, so
 * a split can be rolled back and forth without playing a second twice, and
 * rolled clean past its neighbour, which is then simply gone: a split is
 * only a boundary.
 *
 * When the neighbour cannot make up the difference (it is at its shortest,
 * or at the end of its recording, or there is no neighbour at all because
 * the edge is at the end of the row) the material that would otherwise
 * have vanished becomes its own piece at the seam, there to be deleted.
 * Nothing is ever cut by a drag; only Delete removes anything.
 */
export function trimEdge(
  pieces: Piece[],
  index: number,
  edge: 'start' | 'end',
  to: number,
  duration: number,
  lengthOf: (piece: Piece) => number | null,
): Piece[] {
  const piece = pieces[index];
  if (!piece) return pieces;
  const stub = (start: number, end: number): Piece => {
    const made: Piece = { ...piece, id: newId(piece.kind === 'self' ? 's' : 'c'), start, end };
    delete made.title;
    return made;
  };
  const neighbourIndex = edge === 'end' ? index + 1 : index - 1;
  const neighbour = pieces[neighbourIndex] as Piece | undefined;

  if (!neighbour) {
    const own = trimBounds(pieces, index, duration, lengthOf(piece));
    const next = [...pieces];
    if (edge === 'end') {
      if (to >= piece.end) {
        // Outward, off the end of the row: the piece takes back material of its own.
        next[index] = { ...piece, end: Math.min(to, own.maxEnd) };
        return next;
      }
      const at = Math.max(piece.start + MIN_PIECE_SEC, to);
      if (piece.end - at < MIN_PIECE_SEC) return pieces;
      next.splice(index, 1, { ...piece, end: at }, stub(at, piece.end));
      return next;
    }
    if (to <= piece.start) {
      next[index] = { ...piece, start: Math.max(to, own.minStart) };
      return next;
    }
    const at = Math.min(piece.end - MIN_PIECE_SEC, to);
    if (at - piece.start < MIN_PIECE_SEC) return pieces;
    next.splice(index, 1, stub(piece.start, at), { ...piece, start: at });
    return next;
  }

  // The neighbour holds the same recording's material on the far side of
  // this edge: the two share a boundary and may meet but not overlap.
  const adjacent =
    sameSource(piece, neighbour) &&
    (edge === 'end' ? neighbour.start >= piece.end - EPSILON : neighbour.end <= piece.start + EPSILON);

  if (adjacent) {
    // Rolled clean past the neighbour: it is gone, its material is the
    // piece's, and the drag carries on against whatever comes next.
    const past = edge === 'end' ? to >= neighbour.end - MIN_PIECE_SEC : to <= neighbour.start + MIN_PIECE_SEC;
    if (past) {
      const merged = edge === 'end' ? { ...piece, end: neighbour.end } : { ...piece, start: neighbour.start };
      const without = pieces.filter((_, i) => i !== neighbourIndex).map((one, i) => (i === (index > neighbourIndex ? index - 1 : index) ? merged : one));
      return trimEdge(without, index > neighbourIndex ? index - 1 : index, edge, to, duration, lengthOf);
    }
  }

  const own = trimBounds(pieces, index, duration, lengthOf(piece), adjacent ? neighbourIndex : undefined);
  const bounds = trimBounds(pieces, neighbourIndex, duration, lengthOf(neighbour), adjacent ? index : undefined);
  const next = [...pieces];
  if (edge === 'end') {
    let at = Math.max(piece.start + MIN_PIECE_SEC, Math.min(to, own.maxEnd));
    const wanted = at - piece.end;
    let start = Math.max(bounds.minStart, Math.min(neighbour.start + wanted, neighbour.end - MIN_PIECE_SEC));
    if (adjacent) start = Math.max(start, at);
    const absorbed = start - neighbour.start;
    // Growing takes from the neighbour, and only as much as it has.
    if (wanted > 0 && absorbed < wanted) at = piece.end + absorbed;
    if (adjacent) at = Math.min(at, start);
    // Shrinking gives to the neighbour; what it cannot take stays in the row as a piece of its own.
    const leftover = wanted < 0 ? -wanted + absorbed : 0;
    next[neighbourIndex] = { ...neighbour, start };
    next[index] = { ...piece, end: at };
    if (leftover >= MIN_PIECE_SEC) next.splice(index + 1, 0, stub(at, at + leftover));
  } else {
    let at = Math.min(piece.end - MIN_PIECE_SEC, Math.max(to, own.minStart));
    const wanted = piece.start - at;
    let end = Math.min(bounds.maxEnd, Math.max(neighbour.end - wanted, neighbour.start + MIN_PIECE_SEC));
    if (adjacent) end = Math.min(end, at);
    const absorbed = neighbour.end - end;
    if (wanted > 0 && absorbed < wanted) at = piece.start - absorbed;
    if (adjacent) at = Math.max(at, end);
    const leftover = wanted < 0 ? -wanted + absorbed : 0;
    next[neighbourIndex] = { ...neighbour, end };
    next[index] = { ...piece, start: at };
    if (leftover >= MIN_PIECE_SEC) next.splice(index, 0, stub(at - leftover, at));
  }
  return next;
}

/**
 * Whether the pieces from `from` to `to` (inclusive, in list order) can
 * become one. They can when they are all of one recording and follow each
 * other in it, since one piece cannot hold a reordering. A cut between two
 * of them closes when they merge, so that is allowed only while nothing
 * else plays the cut material; otherwise the merge would play it twice.
 */
export function canMerge(pieces: Piece[], from: number, to: number): boolean {
  const [a, b] = from <= to ? [from, to] : [to, from];
  if (a === b || a < 0 || b >= pieces.length) return false;
  const run = pieces.slice(a, b + 1);
  const others = [...pieces.slice(0, a), ...pieces.slice(b + 1)];
  for (let i = 1; i < run.length; i += 1) {
    const prev = run[i - 1];
    const next = run[i];
    if (!sameSource(run[0], next)) return false;
    if (next.start < prev.end - EPSILON) return false;
    if (next.start > prev.end + EPSILON) {
      const playedElsewhere = others.some(
        (other) => sameSource(other, prev) && other.start < next.start - EPSILON && other.end > prev.end + EPSILON,
      );
      if (playedElsewhere) return false;
    }
  }
  return true;
}

/**
 * The pieces from `from` to `to` as one piece, spanning the first's start
 * to the last's end. It keeps the first piece's chapter title by position,
 * or failing that the first title in the run: the person merging was
 * looking at a run of segments, and the run begins where its first one does.
 */
export function mergePieces(pieces: Piece[], from: number, to: number): Piece[] {
  if (!canMerge(pieces, from, to)) return pieces;
  const [a, b] = from <= to ? [from, to] : [to, from];
  const run = pieces.slice(a, b + 1);
  const title = run.find((piece) => piece.title?.trim())?.title?.trim();
  const merged: Piece = { ...run[0], end: run[run.length - 1].end };
  if (title) merged.title = title;
  else delete merged.title;
  return [...pieces.slice(0, a), merged, ...pieces.slice(b + 1)];
}

/**
 * A range of the original taken out of every self piece that plays it. A
 * piece the range falls inside becomes two; one it covers goes. Null when
 * nothing of the original would be left.
 */
export function subtractRange(pieces: Piece[], [from, to]: Range): Piece[] | null {
  const next: Piece[] = [];
  for (const piece of pieces) {
    if (piece.kind !== 'self' || to <= piece.start + EPSILON || from >= piece.end - EPSILON) {
      next.push(piece);
      continue;
    }
    if (from > piece.start + MIN_PIECE_SEC) next.push({ ...piece, end: from });
    if (to < piece.end - MIN_PIECE_SEC) {
      const rest: Piece = { ...piece, id: newId('s'), start: to };
      if (from > piece.start + MIN_PIECE_SEC) delete rest.title;
      next.push(rest);
    }
  }
  if (!next.some((piece) => piece.kind === 'self')) return null;
  return next;
}

// ---------------------------------------------------------------------------
// The older shape of an edit: sorted kept ranges, and clips each pinned to a
// second of the original. An API build from before ordered pieces still
// sends only this; it becomes the same list the way the API composed it.

export function piecesFromLegacy(keep: Range[] | null, clips: Clip[], duration: number): Piece[] {
  const ranges = keep && keep.length > 0 ? keep : [[0, duration] as Range];
  const ordered = [...clips].sort((a, b) => a.at - b.at);
  const pieces: Piece[] = [];
  let next = 0;
  const pushClip = (clip: Clip) =>
    pieces.push({ kind: 'clip', id: clip.id, videoId: clip.videoId, start: clip.start, end: clip.end });
  for (const [rangeStart, rangeEnd] of ranges) {
    while (next < ordered.length && ordered[next].at <= rangeStart + EPSILON) pushClip(ordered[next++]);
    let cursor = rangeStart;
    while (next < ordered.length && ordered[next].at < rangeEnd - EPSILON) {
      const at = Math.max(ordered[next].at, cursor);
      if (at > cursor + EPSILON) pieces.push({ kind: 'self', id: newId('s'), start: cursor, end: at });
      cursor = at;
      pushClip(ordered[next++]);
    }
    if (rangeEnd > cursor + EPSILON) pieces.push({ kind: 'self', id: newId('s'), start: cursor, end: rangeEnd });
  }
  while (next < ordered.length) pushClip(ordered[next++]);
  return pieces;
}

/** The list an edit state carries, whichever build of the API sent it. */
export function piecesOf(state: { pieces?: Piece[]; keep: Range[] | null; clips: Clip[] }, duration: number): Piece[] {
  return withIds(state.pieces && state.pieces.length > 0 ? state.pieces : piecesFromLegacy(state.keep, state.clips, duration));
}
